var __jsx = React.createElement;
import React, { useState, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parse, isValid as isValidDate, isMatch } from "date-fns";
import { faCalendarDay, faXmark } from "@fortawesome/pro-regular-svg-icons";
import AwesomeIcon from "../../AwesomeIcon";
import { useStoreState } from "pullstate";
import Store from "../../../utils/Store";
import { useTranslation } from "react-i18next";
import { useDropdownScroll } from "../hooks/useDropdownScroll";
import classNames from "classnames";
import { getInputBorderAndBackgroundClasses } from "../../../utils/fieldValidation";
import { validateDateFieldForFutureDate } from "../utils/fieldValidator";
import { parseApiFormat } from "../utils/dateValidatorUtils";
var DateInput = function DateInput(_ref) {
  var value = _ref.value,
    placeholder = _ref.placeholder,
    onChange = _ref.onChange,
    pattern = _ref.pattern,
    formatHint = _ref.formatHint,
    disabled = _ref.disabled,
    isEmpty = _ref.isEmpty,
    isRequired = _ref.isRequired,
    errorMessage = _ref.errorMessage,
    isShouldBeBlankError = _ref.isShouldBeBlankError,
    fieldName = _ref.fieldName;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    isOpen = _useState[0],
    setIsOpen = _useState[1];
  var _useState2 = useState(""),
    dateError = _useState2[0],
    setDateError = _useState2[1];
  var containerRef = useRef(null);
  var _useStoreState = useStoreState(Store, function (s) {
      return {
        isDarkMode: s.isDarkMode
      };
    }),
    isDarkMode = _useStoreState.isDarkMode;
  useDropdownScroll(isOpen, containerRef, 300);
  var shouldShowServerError = !isEmpty && !!errorMessage;
  var shouldShowError = !!dateError || shouldShowServerError;
  var borderAndBackgroundClasses = getInputBorderAndBackgroundClasses(shouldShowError, isRequired !== null && isRequired !== void 0 ? isRequired : false, isEmpty !== null && isEmpty !== void 0 ? isEmpty : false);
  var dateFormat = parseApiFormat(formatHint) || process.env.NEXT_PUBLIC_REGIONAL_DATE_FORMAT || "MM/dd/yyyy";

  // Parse and validate the date value
  var selectedDate = function () {
    if (!value) return null;
    var parsedDate = parse(value, dateFormat, new Date());
    return isValidDate(parsedDate) ? parsedDate : null;
  }();
  var formatDateToString = function formatDateToString(date, format) {
    var month = String(date.getMonth() + 1).padStart(2, "0");
    var day = String(date.getDate()).padStart(2, "0");
    var year = date.getFullYear();
    return format.replace(/yyyy/g, String(year)).replace(/MM/g, month).replace(/dd/g, day);
  };
  var handleChange = function handleChange(date) {
    if (date) {
      var formattedDate = formatDateToString(date, dateFormat);
      onChange(formattedDate);

      // Validate for future dates if applicable
      if (formattedDate && fieldName) {
        var error = validateDateFieldForFutureDate(fieldName, formattedDate, dateFormat);
        if (error) {
          setDateError(error);
          return;
        }
      }
    } else {
      onChange("");
    }
    setDateError("");
    setIsOpen(false);
  };
  var handleChangeRaw = function handleChangeRaw(event) {
    var rawValue = event.target.value;
    if (dateError) {
      setDateError("");
    }

    // Validate when input is complete
    if ((rawValue === null || rawValue === void 0 ? void 0 : rawValue.length) >= (dateFormat === null || dateFormat === void 0 ? void 0 : dateFormat.length)) {
      if (!isMatch(rawValue, dateFormat)) {
        setDateError(t("label.acr.invalid_date_expected_format", {
          dateFormat: dateFormat
        }));
      }
    }
  };
  var handleBlur = function handleBlur() {
    // Trim whitespace on blur if value exists
    if (value && typeof value === "string") {
      var trimmedValue = value.trim();
      if (trimmedValue !== value) {
        onChange(trimmedValue);
      }
    }

    // Validate for future dates if applicable
    if (value && fieldName) {
      var error = validateDateFieldForFutureDate(fieldName, String(value), dateFormat);
      if (error) {
        setDateError(error);
      }
    }
  };
  var handleClear = function handleClear(e) {
    e.preventDefault();
    e.stopPropagation();
    onChange(null);
    setDateError("");
    setIsOpen(false);
  };
  var handleOpenCalendar = function handleOpenCalendar() {
    if (!disabled) {
      setIsOpen(true);
    }
  };
  var handleCloseCalendar = function handleCloseCalendar() {
    setIsOpen(false);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "w-full acr-date-input",
    ref: containerRef
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("relative rounded-lg focus-within:ring-2 focus-within:ring-teal-500 dark:focus-within:ring-teal-400", {
      "opacity-60": disabled
    })
  }, /*#__PURE__*/React.createElement(DatePicker, {
    selected: selectedDate,
    onChange: handleChange,
    onChangeRaw: handleChangeRaw,
    dateFormat: dateFormat,
    placeholderText: placeholder,
    onBlur: handleBlur,
    disabled: disabled,
    open: isOpen,
    onClickOutside: handleCloseCalendar,
    onCalendarClose: handleCloseCalendar,
    onInputClick: handleOpenCalendar,
    shouldCloseOnSelect: true,
    preventOpenOnFocus: true,
    className: classNames("w-full h-[40px] px-4 py-3 border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 cursor-pointer focus:outline-none focus:!border-teal-500 dark:focus:!border-teal-400", borderAndBackgroundClasses, {
      "pr-16": value,
      "pr-10": !value,
      "cursor-not-allowed": disabled
    }),
    calendarClassName: isDarkMode ? "dark-datepicker" : "light-datepicker",
    wrapperClassName: "w-full",
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: "select",
    scrollableYearDropdown: true,
    yearDropdownItemNumber: 150
  }), /*#__PURE__*/React.createElement("div", {
    className: "absolute right-4 top-0 bottom-0 flex items-center gap-1.5 pointer-events-none"
  }, value && !disabled && /*#__PURE__*/React.createElement("button", {
    onClick: handleClear,
    className: "flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors pointer-events-auto",
    title: "Clear date",
    type: "button"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faXmark,
    size: "sm"
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center text-gray-500 dark:text-gray-400 ".concat(disabled ? "opacity-60" : ""),
    title: "Calendar"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faCalendarDay,
    className: "h-5 w-4"
  })))), (dateError || shouldShowServerError) && /*#__PURE__*/React.createElement("div", {
    className: "mt-1 ml-2 text-left text-red-500 dark:text-red-400 text-sm"
  }, dateError || errorMessage), !dateError && !shouldShowServerError && formatHint && !value && /*#__PURE__*/React.createElement("div", {
    className: "mt-1 ml-2 text-left text-gray-600 dark:text-gray-400 text-sm"
  }, formatHint));
};
export default DateInput;